import { useState, useEffect } from 'react'
import Upload from './upload/index'
import MediaLibrary from './mediaLibrary'
import AIImageGeneration from './aiImageGeneration'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'

const SourcePicker = ({ selectedOption, handleChange, campaignFormat }) => {
    return (
        <div className="d-flex flex-column mb-4" style={{ gap: '12px' }}>
            <label className="d-flex align-items-center gap-2">
                <input
                    type="radio"
                    name="imageSource"
                    value="upload"
                    checked={selectedOption === 'upload'}
                    className="form-check-input mt-0 custom-radio"
                    onChange={() => handleChange('upload')}
                />
                Upload from computer
            </label>
            <label className="d-flex align-items-center gap-2">
                <input
                    type="radio"
                    name="imageSource"
                    value="mediaLibrary"
                    checked={selectedOption === 'mediaLibrary'}
                    className="form-check-input mt-0 custom-radio"
                    onChange={() => handleChange('mediaLibrary')}
                />
                Media library selection
            </label>
            <label className="d-flex align-items-center gap-2">
                <input
                    type="radio"
                    name="imageSource"
                    value="aiImage"
                    checked={selectedOption === 'aiImage'}
                    className="form-check-input mt-0 custom-radio"
                    onChange={() => handleChange('aiImage')}
                />
                Generate with AI
            </label>
        </div>
    )
}

const UploadAssets = () => {
    const {
        campaignId,
        campaignFormat,
        id: adId,
        assetAdId,
        registerNextGuard,
        goToStep,
        deregisterNextGuard,
        setStepContentData,
        getStepContentData,
        markStepAs,
        purgeNextGuards,
        setVisible,
        dataForStepper,
    } = useWizard()

    const savedSource = getStepContentData('uploadAssets')?.source
    const [selectedOption, setSelectedOption] = useState(savedSource || 'upload')

    // Save the source to the step content data if it's not already set
    useEffect(() => {
        if (!savedSource) setStepContentData({ source: 'upload' }, 'uploadAssets')

        const nextGuard = registerNextGuard(() => markStepAs(1))
        return () => deregisterNextGuard(nextGuard)
    }, [])

    const handleSourceChange = (source) => {
        purgeNextGuards()
        setSelectedOption(source)
        setStepContentData({ source }, 'uploadAssets')
        setStepContentData({ selectedOption: null }, 'uploadAssets')

        if (source !== 'aiImage') {
            setVisible('aiImageToImage', false)
            setVisible('aiTextToImage', false)
            setVisible('aiAssetLibrary', false)
            setVisible('aiTextToVideo', false)
        }
    }

    const fetchAdId = assetAdId ?? adId

    let component = (
        <Upload
            adId={adId}
            fetchAdId={fetchAdId}
            campaignId={campaignId}
            campaignFormat={campaignFormat}
            registerNextGuard={registerNextGuard}
            setStepContentData={setStepContentData}
            deregisterNextGuard={deregisterNextGuard}
            allowMultiple={campaignFormat === 'carousel'}
        />
    )

    if (selectedOption === 'mediaLibrary') {
        component = (
            <MediaLibrary
                adId={adId}
                fetchAdId={fetchAdId}
                goToStep={goToStep}
                markStepAs={markStepAs}
                campaignId={campaignId}
                campaignFormat={campaignFormat}
                registerNextGuard={registerNextGuard}
                setStepContentData={setStepContentData}
                deregisterNextGuard={deregisterNextGuard}
                allowMultiple={campaignFormat === 'carousel'}
                updateSelectionAsUpload={() => setSelectedOption('upload')}
            />
        )
    }

    if (selectedOption === 'aiImage') component = <AIImageGeneration campaignFormat={campaignFormat} />

    let title = 'Set Images'
    if (campaignFormat === 'video') title = 'Set Videos'
    else if (campaignFormat === 'carousel') title = 'Set Media'

    return (
        <div className="tw:flex tw:h-full">
            <nav className="tw:h-full">
                <WizardSteps adCreative={dataForStepper} currentStep="uploadAssets" />
            </nav>
            <div className="flex-grow-1 p-4 overflow-auto">
                <div style={{ fontSize: '14px' }}>
                    <span className="tw:block tw:mb-4 tw:font-semibold">{title}</span>
                    <SourcePicker
                        selectedOption={selectedOption}
                        campaignFormat={campaignFormat}
                        handleChange={handleSourceChange}
                    />

                    <div>{component}</div>
                </div>
            </div>
        </div>
    )
}

export default UploadAssets
